/**
 * Mounted inside the World Cup stack. Listens to the WC pick-saved event and
 * shows exactly one prompt the first time a user submits a pick:
 *   - Anonymous user → "Don't lose your rank" sign-up nudge (modal — high-stakes
 *     conversion ask, justifies the interruption).
 *   - Real user who hasn't seen the Arena bridge → "Nice pick!" snackbar that
 *     auto-dismisses (low-stakes hint, must not interrupt picking).
 *
 * Both prompts mark per-user storage flags so they never fire twice across
 * sessions. The existing first-pick flag is set on the very first save in the
 * user's lifetime; the bridge flag is set the moment the snackbar fires.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext';
import { useSnackbar } from '../../src/shared/hooks/useSnackbar';
import { useGuestWalkthroughStore } from '../../src/shared/stores/guestWalkthroughStore';
import { useWcPickSavedEventStore } from '../../src/worldcup/stores/wcPickSavedEventStore';
import {
  hasMadeWcFirstPick,
  hasSeenWcToArenaBridge,
  markCreateWalkthroughSeen,
  markWcFirstPickMade,
  markWcToArenaBridgeSeen,
} from '../../src/tutorial/helpers/walkthroughSeenStorage.helper';
import { decidePostFirstPickPrompt } from '../../src/worldcup/helpers/postFirstPickPromptDecision.helper';
import { GuestSignUpAfterPickPrompt } from './GuestSignUpAfterPickPrompt/GuestSignUpAfterPickPrompt';
import {
  getArenaBridgeSnackbarMessage,
  WC_TO_ARENA_BRIDGE_SNACKBAR,
} from './PostFirstPickPromptManager.constants';

export function PostFirstPickPromptManager() {
  const router = useRouter();
  const { user, isSignedIn, isAnonymous, isLoaded } = useSupabaseAuth();
  const { showSnackbar } = useSnackbar();
  const savedCount = useWcPickSavedEventStore((s) => s.savedCount);
  const setBridgePending = useWcPickSavedEventStore((s) => s.setBridgePending);
  const lastProcessedCountRef = useRef(0);
  /** Session-scoped: once true, the guest prompt has already opened this session. */
  const guestPromptShownThisSessionRef = useRef(false);
  /** Session-scoped: once true, the bridge snackbar has already fired this session. */
  const bridgePromptShownThisSessionRef = useRef(false);

  const [showGuestSignUp, setShowGuestSignUp] = useState(false);

  // Publish whether the onboarding bridge is expected on this user's first pick
  // (brand-new real user), snapshotted at mount so the save hook can suppress its
  // "Pick saved" toast on that pick. Mount-time read keeps it race-free against
  // this manager's own savedCount effect (which marks the bridge seen).
  useEffect(() => {
    const userId = user?.id;
    if (!isLoaded || !isSignedIn || isAnonymous || !userId) {
      setBridgePending(false);
      return;
    }
    let cancelled = false;
    void hasSeenWcToArenaBridge(userId).then((seen) => {
      if (!cancelled) setBridgePending(!seen);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, isAnonymous, user?.id, setBridgePending]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (savedCount === 0) return;
    if (savedCount === lastProcessedCountRef.current) return;
    lastProcessedCountRef.current = savedCount;

    let cancelled = false;
    void (async () => {
      // Anonymous guest path: storage flags don't apply, decide immediately.
      if (isAnonymous) {
        if (cancelled) return;
        const decision = decidePostFirstPickPrompt({
          isLoaded,
          isSignedIn,
          isAnonymous: true,
          hasSeenWcToArenaBridge: false,
          guestPromptShownThisSession: guestPromptShownThisSessionRef.current,
          bridgePromptShownThisSession: bridgePromptShownThisSessionRef.current,
        });
        if (decision.showGuestPrompt) {
          guestPromptShownThisSessionRef.current = true;
          setShowGuestSignUp(true);
        }
        return;
      }

      // Real user path: bridge snackbar fires once in this user's lifetime
      // after their first pick. The seen flag is persisted immediately
      // because the snackbar auto-dismisses — there's no "dismiss" event.
      const userId = user?.id;
      if (!userId) return;
      const [alreadyFirstPick, alreadySeenBridge] = await Promise.all([
        hasMadeWcFirstPick(userId),
        hasSeenWcToArenaBridge(userId),
      ]);
      if (cancelled) return;
      if (!alreadyFirstPick) {
        await markWcFirstPickMade(userId);
        if (cancelled) return;
      }
      const decision = decidePostFirstPickPrompt({
        isLoaded,
        isSignedIn,
        isAnonymous: false,
        hasSeenWcToArenaBridge: alreadySeenBridge,
        guestPromptShownThisSession: guestPromptShownThisSessionRef.current,
        bridgePromptShownThisSession: bridgePromptShownThisSessionRef.current,
      });
      if (decision.showBridge) {
        bridgePromptShownThisSessionRef.current = true;
        showSnackbar(
          getArenaBridgeSnackbarMessage(),
          'success',
          WC_TO_ARENA_BRIDGE_SNACKBAR.TITLE,
          {
            durationMs: WC_TO_ARENA_BRIDGE_SNACKBAR.DURATION_MS,
            action: {
              label: WC_TO_ARENA_BRIDGE_SNACKBAR.ACTION_LABEL,
              onPress: () => {
                // Mark the create walkthrough seen NOW so Arena's first-visit
                // auto-fire effect won't re-trigger it after this run finishes.
                // Then queue the walkthrough so it auto-starts on Arena mount.
                void markCreateWalkthroughSeen(userId);
                useGuestWalkthroughStore.getState().setPendingWalkthroughStart(true);
                router.push('/app/arena' as `/${string}`);
              },
            },
          },
        );
        await markWcToArenaBridgeSeen(userId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [savedCount, isLoaded, isSignedIn, isAnonymous, user?.id, showSnackbar, router]);

  const dismissGuestSignUp = useCallback(() => {
    setShowGuestSignUp(false);
  }, []);

  return (
    <GuestSignUpAfterPickPrompt
      visible={showGuestSignUp}
      onDismiss={dismissGuestSignUp}
    />
  );
}
